import {action, computed, makeObservable, observable} from "mobx";
import {IExampleValue, RuleInput} from "./RuleInput";
import ProductRulesApi, {ISavedRule} from "../../../api/ProductRulesApi";
import {IRuleInputOption} from "../../../components/rule/AsyncRuleInputSelection";
import {GroupBase} from "react-select";
import {noticeController} from "../../../utils/NoticeController"
import {AxiosPromise} from "axios"

export interface IRuleObj {
    name: string
    is_required?: boolean
    description?: string
    markups?: IRuleMarkups[]
    ozon_attribute_id?: string
    ozon_category_id?: string
    saved_rule: ISavedRule | null
    input_units?: IRuleUnitOption[]
    type: string
    children_rules?: IRuleObj[]
    save?: boolean
}
export interface IRuleUnitOption {
    label: string, value: string
}

export interface IRuleMarkups {
    from: {source_value:string, source_type:string}
    to: {source_value:string, source_type:string}
    additional_price: {source_value:string, source_type:string}
    additional_price2: {source_value:string, source_type:string}
    additional_price_currency: string
    additional_price_currency2: string
}
export default class Rule {
    ozon_attribute_id?: number;
    defaultOption?: GroupBase<IRuleInputOption>[];
    _markups?: IRuleMarkups[]

    constructor(rule: IRuleObj) {
        this._name = rule.name
        this._is_required = rule.is_required
        this._description = rule.description
        this._type = rule.type
        if (rule.ozon_attribute_id) {
            this.ozon_attribute_id = +rule.ozon_attribute_id
        }
        if (rule.ozon_category_id) {
            this._category_id = +rule.ozon_category_id
        }
        if (rule.markups) {
            this._markups = rule.markups
        }
        if (rule.save) {
            this._save = true
        }
        this._input = new RuleInput(this, rule.saved_rule, rule.input_units)
        makeObservable(this, {
            defaultOption: observable,
            _input: observable,
            _markups: observable,
            markups: computed,
            input: computed,
            exampleValue: computed,
            isInputExist: computed
        })
    }

    private _save: boolean = false;

    get save(): boolean {
        return this._save;
    }

    private _category_id?: number;

    get category_id(): number | undefined {
        return this._category_id;
    }

    _input: RuleInput;

    get input(): RuleInput {
        return this._input;
    }

    get exampleValue(): IExampleValue | undefined {
        return this._input.exampleValue
    }

    private _type: string;

    get type(): string {
        return this._type;
    }

    private _description?: string;

    get description(): string | undefined {
        return this._description;
    }

    private _is_required: boolean | undefined;

    get is_required(): boolean | undefined {
        return this._is_required;
    }

    private _name: string;

    get name(): string {
        return this._name;
    }

    _child_rules: Rule[] = []

    get child_rules(): Rule[] {
        return this._child_rules;
    }

    get markups(): IRuleMarkups[] | undefined {
        return this._markups;
    }

    deleteMarkup(index: number): void {
        this._markups && this._markups.splice(index, 1)
        this.setMarkupRule()
    }

    addMarkup(): void {
        if(!this._markups) this._markups = []
        this._markups.push({
            from: {source_value: "", source_type: 'IS_EMPTY'},
            to:  {source_value: "", source_type: 'IS_EMPTY'},
            additional_price: {source_value: "", source_type: 'IS_EMPTY'},
            additional_price2: {source_value: "", source_type: 'IS_EMPTY'},
            additional_price_currency: '%',
            additional_price_currency2: '%'
        })
        this.setMarkupRule()
    }

    setMarkupRule(input_type = "", input_name = ""): void {
        if (this.input.rule) {
            input_type = input_type ? input_type : this.input.rule.input_type
            input_name = input_name ? input_name : this.input.rule.input_name
        }

            ProductRulesApi.saveValueOutput({
                input_type,
                input_name,
                // input_unit: "",
                ozon_attribute_id: this.ozon_attribute_id,
                ozon_category_id: this._category_id,
                output_type: this.type,
                markups: this.markups
            }).then(action((result) => {
                let saved_rule = null;
                if (result.data.data?.saved_rule) {
                    saved_rule = result.data.data.saved_rule
                }
                this._input = new RuleInput(this, saved_rule)
                this._input.loadExampleValue()
            }))

    }

    loadDefaultOption() {
        this.getOptions().then(action((options) => {
            this.defaultOption = options;
        }))
    }

    getOptions(inputValue?: string) {
        return ProductRulesApi.getValidInputs({
            query: inputValue,
            output_type: this.type,
            ozon_attribute_id: this.ozon_attribute_id,
            ozon_category_id: this._category_id
        }).then((result) => {
            return result.data.data
        })
    }

    setInputRule() {
        const input_unit = this.input.input_unit_option ? this.input.input_unit_option.value : undefined
        // console.log(this.input)
        if (this.input.rule) {

            return ProductRulesApi.saveValueOutput({
                input_type: this.input.rule.input_type,
                input_name: this.input.rule.input_name,
                input_unit,
                ozon_attribute_id: this.ozon_attribute_id,
                ozon_category_id: this._category_id,
                output_type: this.type
            })
        }

        return new Promise((resolve, reject) => {})
    }

    setRuleInput({input_type, input_name, input_title}: IRuleInputOption) {
        const input_unit = this.input.input_unit_option

        if(this.markups) {
            // console.log(this.markups)
            this.setMarkupRule(input_type, input_name)
        }

        // return ProductRulesApi.saveValueOutput({
        //     input_type, input_name, input_unit:"",
        //     ozon_attribute_id: this.ozon_attribute_id,
        //     ozon_category_id: this._category_id,
        //     output_type: this.type,
        //     markups: this.markups
        // }).then(action((result) => {
        //     let saved_rule = null;
        //     if (result.data.data?.saved_rule) {
        //         saved_rule = result.data.data.saved_rule
        //     }
        //     this._input.loadExampleValue()
        // }))
        const input_units = this.input.input_unit_options
        const saved_rule = {
                input_type: input_type,
                input_name: input_name,
                input_title: input_title,
                input_unit_option: input_unit ? input_unit : null,
                ozon_attribute_id: String(this.ozon_attribute_id),
                ozon_category_id: String(this._category_id),
                output_type: this.type,
                id: 'new',
                output_unit:null
        }
            this._input = new RuleInput(this, saved_rule, input_units)
            // this._input.loadExampleValue()
        // }))
    }

    deleteRuleInput() {
        if(this.save) {
            return ProductRulesApi.deleteRule({
                ozon_attribute_id: this.ozon_attribute_id,
                ozon_category_id: this._category_id,
                output_type: this.type
            }).then(action((result) => {
                const data = result.data.data
                let saved_rule = null;
                if (data?.saved_rule) {
                    saved_rule = data.saved_rule
                }
                this._input = new RuleInput(this, saved_rule)
            }))
        } else {
            this._input = new RuleInput(this, null)
        }
    }

    callInputLoadExampleValue() {
        this._input.loadExampleValue();
    }

    get isInputExist() {
        return this._input.isInputValueExist
    }
}